/**
 * SeedBatchControls - Controls for seeding and managing test batches
 *
 * Provides UI for creating golden test batches with different scenarios:
 * - single_pod: 4 single-line POD orders
 * - single_pap: 2 single-line P&P orders
 * - multi_mixed: 2 multi-line POD+P&P orders
 * - pretreat: 3 orders with dark colors (requires pretreat)
 * - full_coverage: 11 orders covering all scenarios
 *
 * Also provides cleanup controls to delete old test data.
 *
 * Features for warehouse UX:
 * - Large touch targets for gloved hands
 * - Clear feedback on success/failure
 * - Confirmation for destructive actions
 *
 * Phase 17: Testing Infrastructure
 */
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import { useLocation } from '@/contexts/LocationContext';
import { trpc } from '@/lib/trpc';
import { Loader2, Beaker, Trash2, Sparkles } from 'lucide-react';

/** Batch type options with descriptions */
const BATCH_TYPES = [
  {
    value: 'single_pod',
    label: 'Single POD (4 orders)',
    description: '4 single-line print-on-demand orders',
  },
  {
    value: 'single_pap',
    label: 'Single P&P (2 orders)',
    description: '2 single-line pick & pack orders',
  },
  {
    value: 'multi_mixed',
    label: 'Multi Mixed (2 orders)',
    description: '2 multi-line orders with POD + P&P items',
  },
  {
    value: 'pretreat',
    label: 'Pretreat (3 orders)',
    description: '3 orders with dark colors requiring pretreat',
  },
  {
    value: 'full_coverage',
    label: 'Full Coverage (11 orders)',
    description: 'All scenarios: single, multi, POD, P&P, pretreat',
  },
  {
    value: 'packing_ready',
    label: 'Packing Ready (4 orders)',
    description: '4 orders in bins ready for packing station',
  },
] as const;

type BatchType = typeof BATCH_TYPES[number]['value'];

export function SeedBatchControls() {
  const { selectedLocation } = useLocation();
  const locationId = selectedLocation?.id;
  const { toast } = useToast();
  const utils = trpc.useUtils();

  const [selectedType, setSelectedType] = useState<BatchType>('single_pod');

  // Seed mutation
  const seedMutation = trpc.testing.seedGoldenBatch.useMutation({
    onSuccess: (result) => {
      toast({
        title: 'Test batch created',
        description: `Batch #${result.batchNumber}: ${result.orderCount} orders, ${result.garmentCount} garments`,
      });
      // Invalidate queries to refresh data
      utils.testing.getTestGarmentStatus.invalidate();
    },
    onError: (error) => {
      toast({
        title: 'Failed to create test batch',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Delete mutation
  const deleteMutation = trpc.testing.deleteTestData.useMutation({
    onSuccess: (result) => {
      toast({
        title: 'Test data deleted',
        description: `Deleted ${result.deletedOrders} orders and ${result.deletedGarments} garments`,
      });
      // Invalidate queries to refresh data
      utils.testing.getTestGarmentStatus.invalidate();
    },
    onError: (error) => {
      toast({
        title: 'Failed to delete test data',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const handleSeed = () => {
    if (!locationId) return;
    seedMutation.mutate({
      locationId,
      batchType: selectedType,
    });
  };

  const handleDelete = () => {
    if (!locationId) return;
    deleteMutation.mutate({
      locationId,
      olderThanDays: 0,
    });
  };

  const isLoading = seedMutation.isPending || deleteMutation.isPending;

  return (
    <div className="flex flex-wrap items-center gap-3 p-4 bg-yellow-50/50 dark:bg-yellow-950/10 border-b">
      {/* Batch type selector */}
      <div className="flex items-center gap-2">
        <Beaker className="h-4 w-4 text-yellow-600" />
        <span className="text-sm font-medium text-muted-foreground">
          Seed:
        </span>
        <Select
          value={selectedType}
          onValueChange={(v) => setSelectedType(v as BatchType)}
          disabled={isLoading}
        >
          <SelectTrigger className="w-[260px] h-10">
            <SelectValue placeholder="Select batch type" />
          </SelectTrigger>
          <SelectContent>
            {BATCH_TYPES.map((type) => (
              <SelectItem key={type.value} value={type.value}>
                <span>{type.label}</span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Seed button */}
      <Button
        onClick={handleSeed}
        disabled={!locationId || isLoading}
        variant="default"
        className="h-10 gap-2"
      >
        {seedMutation.isPending ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Sparkles className="h-4 w-4" />
        )}
        Create Test Batch
      </Button>

      {/* Spacer */}
      <div className="flex-1" />

      {/* Delete button with confirmation */}
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button
            variant="outline"
            className="h-10 gap-2 text-destructive hover:text-destructive"
            disabled={!locationId || isLoading}
          >
            {deleteMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Trash2 className="h-4 w-4" />
            )}
            Clean Up
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete test data?</AlertDialogTitle>
            <AlertDialogDescription>
              This will delete all test batches and garments at this location.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Location badge */}
      {selectedLocation && (
        <Badge variant="outline" className="text-xs">
          {selectedLocation.name}
        </Badge>
      )}
    </div>
  );
}
